import { useState, useCallback, createContext, useContext } from 'react'
import Dialog from '../ui/Dialog'
import { OpsButton } from '../ui/ops'

const ConfirmContext = createContext(null)

/**
 * Wrap your app (or a subtree) with <ConfirmProvider> to enable useConfirm().
 *
 * Usage in any child component:
 *   const confirm = useConfirm()
 *   const ok = await confirm('Delete this agent?')
 *   if (ok) { ... }
 *
 * Options:
 *   await confirm('Are you sure?', {
 *     title: 'Confirm Delete',
 *     confirmText: 'Delete',
 *     confirmColor: 'danger',
 *     cancelText: 'Cancel',
 *   })
 */
export function ConfirmProvider({ children }) {
  const [state, setState] = useState({
    visible: false,
    message: '',
    title: 'Confirm',
    confirmText: 'Confirm',
    confirmColor: 'danger',
    cancelText: 'Cancel',
    resolve: null,
  })

  const confirm = useCallback((messageOrOptions, options = {}) => {
    const normalized = (
      messageOrOptions &&
      typeof messageOrOptions === 'object' &&
      !Array.isArray(messageOrOptions) &&
      !('$$typeof' in messageOrOptions)
    )
      ? messageOrOptions
      : { ...options, message: messageOrOptions }

    return new Promise((resolve) => {
      setState({
        visible: true,
        message: normalized.message || '',
        title: normalized.title || 'Confirm',
        confirmText: normalized.confirmText || 'Confirm',
        confirmColor: normalized.confirmColor || 'danger',
        cancelText: normalized.cancelText || 'Cancel',
        resolve,
      })
    })
  }, [])

  const handleClose = useCallback((result) => {
    setState((prev) => {
      if (prev.resolve) prev.resolve(result)
      return { ...prev, visible: false, resolve: null }
    })
  }, [])

  const cancel = useCallback(() => handleClose(false), [handleClose])

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog
        open={state.visible}
        title={state.title}
        onClose={cancel}
        footer={<>
          <OpsButton tone="secondary" onClick={cancel}>{state.cancelText}</OpsButton>
          <OpsButton tone={state.confirmColor === 'danger' ? 'danger' : 'primary'} onClick={() => handleClose(true)}>{state.confirmText}</OpsButton>
        </>}
      >
        {state.message}
      </Dialog>
    </ConfirmContext.Provider>
  )
}

export function useConfirm() {
  const confirm = useContext(ConfirmContext)
  if (!confirm) {
    throw new Error('useConfirm must be used within a <ConfirmProvider>')
  }
  return confirm
}
